import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageOff, X } from '@/components/icons';

/**
 * Small square item thumbnail used in line-item tables across the pipeline.
 * Shows the item-master image, or a neutral placeholder when there's none
 * (and on load error). Renders the raw `imageUrl` just like the Items /
 * Catalogue pages.
 *
 * By default an item that has an image is clickable and opens the picture
 * enlarged in a lightbox. Pass `preview={false}` for thumbnails living inside
 * a clickable row (e.g. picker dropdowns) so the image click doesn't hijack
 * that row's own action.
 */
export function ItemThumb({
  src,
  size = 32,
  preview = true,
}: {
  src?: string | null;
  size?: number;
  preview?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const dim = { height: `${size}px`, width: `${size}px` };

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (src) {
    const img = (
      <img
        src={src}
        alt=""
        loading="lazy"
        style={dim}
        className="shrink-0 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] object-cover"
        onError={(e) => {
          const el = e.currentTarget;
          el.onerror = null;
          el.replaceWith(
            Object.assign(document.createElement('div'), {
              className:
                'shrink-0 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)]',
              style: `height:${size}px;width:${size}px`,
            }),
          );
        }}
      />
    );

    if (!preview) return img;

    return (
      <>
        <button
          type="button"
          className="shrink-0 cursor-zoom-in rounded-md leading-none outline-none focus:ring-2 focus:ring-accent"
          title="Preview image"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {img}
        </button>
        {open &&
          createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
              onMouseDown={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              <button
                type="button"
                className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                title="Close"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                <X size={20} />
              </button>
              <img
                src={src}
                alt=""
                className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>,
            document.body,
          )}
      </>
    );
  }
  return (
    <span
      style={dim}
      className="flex shrink-0 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-muted)]"
    >
      <ImageOff size={Math.round(size * 0.44)} />
    </span>
  );
}
